import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Select, Table, message } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { FiRefreshCw } from 'react-icons/fi';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import PermissionGate from '../../../../components/auth/PermissionGate';
import { useAuth } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import tradeCommitmentService from '../../../../services/tradeCommitmentService';
import { getTradeCommitmentColumns, apiErrMsg } from './tradeCommitment.helpers';
import {
  DIRECTION_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from './tradeCommitment.constants';

export default function TradeCommitmentsListPage() {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_MANAGE);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [direction, setDirection] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const fetchList = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    setError('');
    try {
      const result = await tradeCommitmentService.list({
        page,
        limit: pageSize,
        search: search.trim() || undefined,
        status: status || undefined,
        direction: direction || undefined,
        sort_by: 'id',
        sort_order: 'desc',
      });
      setRows(result.data || []);
      setPagination({
        current: result.pagination?.page || page,
        pageSize: result.pagination?.limit || pageSize,
        total: result.pagination?.total || 0,
      });
    } catch (err) {
      setError(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [search, status, direction]);

  useEffect(() => {
    fetchList(pagination.current, pagination.pageSize);
  }, [fetchList]);

  const columns = useMemo(
    () => [
      ...getTradeCommitmentColumns(),
      {
        title: '',
        key: 'actions',
        render: (_, row) => (
          <Button type="link" onClick={() => navigate(`/pre-ipo/trade-commitments/${row.id}`)}>
            View
          </Button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <DashboardPageWrapper title="Trade Commitments">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Provisional buy/sell deals before formal orders or purchase deal sheets.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate('/pre-ipo/trade-commitments/summary')}>Ledger summary</Button>
          <Button icon={<FiRefreshCw />} onClick={() => fetchList(pagination.current, pagination.pageSize)}>
            Refresh
          </Button>
          <PermissionGate permission={PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_MANAGE}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/pre-ipo/trade-commitments/create')}>
              New commitment
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search counterparty or product"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => fetchList(1, pagination.pageSize)}
          style={{ width: 260 }}
        />
        <Select
          value={status}
          onChange={setStatus}
          options={STATUS_FILTER_OPTIONS}
          style={{ width: 160 }}
        />
        <Select
          value={direction}
          onChange={setDirection}
          options={DIRECTION_FILTER_OPTIONS}
          style={{ width: 160 }}
        />
        <Button onClick={() => fetchList(1, pagination.pageSize)}>Apply</Button>
      </div>

      {error && <Alert type="error" message={error} className="mb-4" showIcon />}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          onChange: (page, pageSize) => fetchList(page, pageSize),
        }}
      />
    </DashboardPageWrapper>
  );
}
